import { CovFeatureFlagProvider } from "@cx-component-library/react-components/feature-flags";
import { type AmplifyAppConfig, configureAmplify } from "config/amplify";
import { AppConfigProvider } from "config/app-config";
import { appendSiteMinderBypass } from "config/axios-interceptors";
import {
  APP_CONFIG_FLAGS_FALLBACK,
  type AppConfigFeatureFlags,
} from "feature-flags";
import { loadCdcScriptDirect } from "hooks/use-cdc-script";
import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, useLocation } from "react-router-dom";
import App from "./App";
import "./index.css";
import "./i18n/i18n";
import { initializeDatadog } from "./datadog";

// CX-ADR-019 AppConfig feature flags served at `/v0/feature-flags` (public, no
// auth). On localhost the CRA proxy forwards to the QA gateway, so the
// SiteMinder bypass param is appended when configured.
const FEATURE_FLAGS_ENDPOINT = appendSiteMinderBypass("/v0/feature-flags");

// Function to clean up SAML response and related parameters from URL
function cleanupSamlResponseFromUrl() {
  const url = window.location.href;
  const hasParams = url.includes("?");

  if (hasParams) {
    // Check if URL has SAML parameters
    const hasSamlParams =
      url.includes("SAMLResponse=") ||
      url.includes("RelayState=") ||
      url.includes("SigAlg=") ||
      url.includes("Signature=");

    if (hasSamlParams) {
      // Remove all query parameters and replace with a clean URL
      const baseUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, baseUrl);
    }
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function startApplication(amplify: AmplifyAppConfig) {
  // Clean up SAML parameters before rendering the app
  cleanupSamlResponseFromUrl();

  // Load CDC script early in the application lifecycle
  loadCdcScriptDirect();

  // Initialize Datadog RUM
  initializeDatadog().catch((error) => {
    console.error("Failed to initialize Datadog:", error);
  });

  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <BrowserRouter>
        <ScrollToTop />
        <CovFeatureFlagProvider<AppConfigFeatureFlags>
          endpoint={FEATURE_FLAGS_ENDPOINT}
          fallback={APP_CONFIG_FLAGS_FALLBACK}
        >
          <AppConfigProvider>
            <App signInIdentityProvider={amplify.signInIdentityProvider} />
          </AppConfigProvider>
        </CovFeatureFlagProvider>
      </BrowserRouter>
    </React.StrictMode>,
  );
}

configureAmplify().then(startApplication);
